import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@clerk/react";
import { RotateCcwIcon, Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCampaignStore } from "@/stores";
import { useEveActionsStore } from "@/stores/eve/eveActionsStore";
import { evePhaseLabel, isCustomPhaseKey } from "@/lib/eve";
import { EvePipelinePhase } from "@/types/eve";
import { phaseNoun } from "./shared";

/** A reset / delete picked from a phase's ⋯ menu in the nav, waiting on its confirm dialog. */
export interface PhaseAction {
  kind: "reset" | "delete";
  phase: EvePipelinePhase;
  /** The pipeline `phase` belongs to (labels resolve against it). */
  pipeline: EvePipelinePhase[];
  /** Tiers the write targets: [tier] in the per-tier view, null = every tier. */
  targetTiers: number[] | null;
  /** The nav group's tier (null = shared / single-tier) — to tell whether the open section was deleted. */
  tier: number | null;
}

/**
 * The confirm dialog behind a phase's Reset to defaults / Delete menu items.
 * Open while `action` is set; stays open (with the executor's refusal
 * toasted) when the write is refused, e.g. a delete while creators are
 * inside the phase.
 */
export function PhaseActionDialog({
  action,
  onClose,
  onDeleted,
}: {
  action: PhaseAction | null;
  onClose: () => void;
  onDeleted: (action: PhaseAction) => void;
}) {
  const { campaignId } = useCampaignStore();
  const { getToken, orgId } = useAuth();
  const { updateCampaignPhases, isSavingConfig } = useEveActionsStore();

  // The last action keeps the dialog's text while it animates closed.
  const [last, setLast] = useState(action);
  useEffect(() => {
    if (action) setLast(action);
  }, [action]);
  const shown = action ?? last;
  if (!shown) return null;

  const { kind, phase, pipeline, targetTiers } = shown;
  const label = evePhaseLabel(phase, pipeline);
  const noun = phaseNoun(phase.key);
  const isContract = phase.key === "contract";

  const run = async () => {
    const token = await getToken();
    if (!token || !campaignId || !orgId) return console.error("No token found");
    const patch = kind === "reset" ? { key: phase.key, reset_settings: true } : { key: phase.key, remove: true };
    const ok = await updateCampaignPhases(campaignId, [patch], orgId, token, { tiers: targetTiers });
    if (!ok) return;
    onClose();
    if (kind === "delete") onDeleted(shown);
  };

  return (
    <AlertDialog open={action != null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="max-w-lg">
        {kind === "reset" ? (
          <AlertDialogHeader>
            <AlertDialogTitle>Reset {label} settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This phase's settings return to Amy's defaults
              {targetTiers?.length === 1 ? ` in Tier ${targetTiers[0]}` : ""}. Phase instructions
              {isContract ? ", the template and the prefill mapping" : ""} are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
        ) : (
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes this {noun.toLowerCase()} from{" "}
              {targetTiers?.length === 1 ? `Tier ${targetTiers[0]}'s` : "the"} pipeline
              {isCustomPhaseKey(phase.key)
                ? "."
                : ` — its settings${
                    isContract ? ", template and prefill mapping" : ""
                  } go with it. You can add it back from the + later, with default settings.`}{" "}
              Refused while creators are inside it. To just pause it, switch it off instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {kind === "reset" ? (
            <Button disabled={isSavingConfig} onClick={run}>
              {isSavingConfig ? <Spinner /> : <RotateCcwIcon className="size-3.5" />}
              Reset Settings
            </Button>
          ) : (
            <ScopeWrapper>
              <Button
                variant="destructive"
                disabled={isSavingConfig}
                onClick={run}
              >
                {isSavingConfig ? <Spinner /> : <Trash2Icon className="size-3.5" />}
                Delete {noun}
              </Button>
            </ScopeWrapper>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
